import { useEffect, useState } from "react";
import API from "../../../services/api";
import html2pdf from "html2pdf.js";
import "./timetabelview.css";

export default function WeeklyTimetableList() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"))?.school;

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const [filter, setFilter] = useState({
    class_id: "",
    section_id: "",
  });

  // ✅ EDIT MODAL STATE
  const [editModal, setEditModal] = useState({
    open: false,
    period: null,
    subject: "",
    teacher_id: "",
  });

  const days = [
    "monday", "tuesday", "wednesday",
    "thursday", "friday", "saturday", "sunday"
  ];

  // ================= LOAD TEACHERS =================
  useEffect(() => {
    if (!school?.id) return;

    API.get("/school-admin/teachers/", {
      headers: { "X-SCHOOL-ID": school.id },
    }).then((res) => setTeachers(res.data.teachers || []));
  }, [school?.id]);

  // ================= LOAD CLASSES =================
  useEffect(() => {
    if (!school?.id) return;

    API.get("/school-admin/classes/", {
      headers: { "X-SCHOOL-ID": school.id },
    }).then((res) => setClasses(res.data.classes || []));
  }, [school?.id]);

  // ================= LOAD SECTIONS =================
  useEffect(() => {
    if (!filter.class_id) {
      setSections([]);
      return;
    }

    API.get(`/school-admin/sections/?class_id=${filter.class_id}`, {
      headers: { "X-SCHOOL-ID": school.id },
    }).then((res) => setSections(res.data.sections || []));
  }, [filter.class_id]);

  // ================= LOAD TIMETABLE =================
  const loadTimetable = async () => {
    if (!filter.class_id || !filter.section_id) {
      return alert("Select Class & Section");
    }

    setLoading(true);
    setSearched(true);

    try {
      const res = await API.get(
        `/school-admin/weekly-timetable/view/?class_id=${filter.class_id}&section_id=${filter.section_id}`,
        {
          headers: { "X-SCHOOL-ID": school.id },
        }
      );

      setSessions(res.data.sessions || []);
    } catch {
      alert("Failed to load timetable");
    }

    setLoading(false);
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this period?")) return;

    await API.delete(`/school-admin/timetable/period/${id}/`, {
      headers: { "X-SCHOOL-ID": school.id },
    });

    setSessions(prev =>
      prev.map(s => ({
        ...s,
        periods: s.periods.filter(p => p.id !== id),
      }))
    );
  };

  // ================= OPEN EDIT =================
  const openEditModal = (period) => {
    setEditModal({
      open: true,
      period: period,
      subject: period.subject,
      teacher_id: period.teacher_id || "",
    });
  };

  // ================= SAVE EDIT =================
  const saveEdit = async () => {
    try {
      await API.patch(
        `/school-admin/timetable/period/${editModal.period.id}/`,
        {
          subject: editModal.subject,
          teacher_id: editModal.teacher_id,
        },
        {
          headers: { "X-SCHOOL-ID": school.id },
        }
      );

      setSessions(prev =>
        prev.map(s => ({
          ...s,
          periods: s.periods.map(p =>
            p.id === editModal.period.id
              ? {
                ...p,
                subject: editModal.subject,
                teacher_id: editModal.teacher_id,
                teacher:
                  teachers.find(t => t.id == editModal.teacher_id)?.name || "—",
              }
              : p
          ),
        }))
      );

      setEditModal({ open: false });

    } catch {
      alert("Update failed");
    }
  };

  // ================= DOWNLOAD PDF =================
  const handleDownload = () => {
    const element = document.querySelector(".tt-table");

    html2pdf().set({
      margin: 5,
      filename: "timetable.pdf",
      html2canvas: { scale: 3 },
      jsPDF: { orientation: "landscape" },
    }).from(element).save();
  };

  // ================= FORMAT =================
  const formatted = {};
  sessions.forEach((s) => {
    if (!formatted[s.day]) formatted[s.day] = {};
    if (!formatted[s.day][s.session]) formatted[s.day][s.session] = {};

    s.periods.forEach((p) => {
      formatted[s.day][s.session][p.period] = p;
    });
  });

  const maxPeriods = Math.max(
    ...sessions.flatMap((s) => s.periods.map((p) => p.period)),
    0
  );

  return (
    <div className="tt-page timetable-weekly-page">

      {/* HEADER */}
      <div className="tt-header">
        <div>
          <span>Weekly planner</span>
          <h2>Weekly Timetable</h2>
          <p>Review periods across the week, update teachers, and export a PDF.</p>
        </div>

        {sessions.length > 0 && (
          <button onClick={handleDownload} className="tt-download-btn">
            Download PDF
          </button>
        )}
      </div>

      {/* FILTER */}
      <div className="tt-form">
        <select
          value={filter.class_id}
          onChange={(e) =>
            setFilter({ ...filter, class_id: e.target.value })
          }
        >
          <option value="">Class</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          value={filter.section_id}
          onChange={(e) =>
            setFilter({ ...filter, section_id: e.target.value })
          }
        >
          <option value="">Section</option>
          {sections.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <button onClick={loadTimetable}>
          {loading ? "Loading..." : "View"}
        </button>
      </div>

      {loading && <div className="tt-empty-state">Loading timetable...</div>}

      {!loading && sessions.length === 0 && (
        <div className="tt-empty-state">
          <div className="empty-icon">TT</div>
          <h4>{searched ? "No timetable found" : "Select a class and section"}</h4>
          <p>
            {searched
              ? "Create periods from the timetable dashboard or try another section."
              : "Use the filters above to view the complete weekly timetable."}
          </p>
        </div>
      )}

      {/* TABLE */}
      {!loading && sessions.length > 0 && (
        <div className="tt-preview">
          <table className="tt-table">
            <thead>
              <tr>
                <th>Day</th>
                {[...Array(maxPeriods)].map((_, i) => (
                  <th key={i}>P{i + 1}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {days.map(day => (
                <tr key={day}>
                  <td className="tt-day">{day.toUpperCase()}</td>

                  {[...Array(maxPeriods)].map((_, i) => {
                    const p = formatted[day]?.morning?.[i + 1] ||
                      formatted[day]?.evening?.[i + 1];

                    return (
                      <td key={i}>
                        {p ? (
                          <div
                            className="tt-cell"
                            style={{
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",   // ✅ FIX
                              justifyContent: "flex-start", // ✅ FIX
                              padding: "6px 26px 6px 6px", // space for buttons
                              minHeight: "60px",
                            }}
                          >
                            <b>{p.subject}</b>
                            <small>{p.teacher}</small>

                            <div style={actionStyles.wrapper}>
                              <button
                                onClick={() => openEditModal(p)}
                                style={{ ...actionStyles.btn, ...actionStyles.edit }}
                              >
                                ✎
                              </button>

                              <button
                                onClick={() => handleDelete(p.id)}
                                style={{ ...actionStyles.btn, ...actionStyles.delete }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : "-"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* EDIT MODAL */}
      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal-box">

            <h3>Edit Period</h3>

            <input
              value={editModal.subject}
              onChange={(e) =>
                setEditModal({ ...editModal, subject: e.target.value })
              }
            />

            <select
              value={editModal.teacher_id}
              onChange={(e) =>
                setEditModal({ ...editModal, teacher_id: e.target.value })
              }
            >
              <option value="">Teacher</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            <div className="modal-actions">
              <button onClick={saveEdit}>Save</button>
              <button onClick={() => setEditModal({ open: false })}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
const actionStyles = {
  wrapper: {
    position: "absolute",
    top: "4px",
    right: "4px",
    display: "flex",
    gap: "3px",
    zIndex: 5,
  },

  btn: {
    width: "18px",
    height: "18px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    padding: 0,
  },

  edit: {
    background: "#22c55e",
    color: "#fff",
  },

  delete: {
    background: "#ef4444",
    color: "#fff",
  },
};
